/**
 * Shared device connectivity probe utilities.
 *
 * ALL direct-to-device operations (sync, configure, health check) must use these
 * functions instead of duplicating IP selection and probe logic.
 *
 * Strategy:
 *  1. If Electron's `deviceQuickProbe` IPC is available → TCP connect test (~400ms local, ~1500ms public)
 *  2. If not (old Electron build without restart, or web context) → HTTP GET fallback (6s)
 */

export interface DeviceAddresses {
  local_ip_address?: string;
  ip_address?: string;
  port?: number | string;
}

/**
 * Parse host and port from an ip_address string.
 * Handles protocol prefixes ("http://10.0.0.180:8180") and bare "host:port" forms.
 * Returns null if the input is empty or unparseable.
 */
export function parseDeviceHostPort(
  ip: string,
  fallbackPort?: number | string
): [string, number] | null {
  const raw = ip.replace(/^https?:\/\//, '').trim();
  if (!raw) return null;
  const colonIdx = raw.lastIndexOf(':');
  if (colonIdx > 0) {
    const host = raw.substring(0, colonIdx);
    const port = parseInt(raw.substring(colonIdx + 1), 10);
    if (host && !isNaN(port) && port > 0) return [host, port];
  }
  const fb = fallbackPort !== undefined ? parseInt(String(fallbackPort), 10) : 80;
  return [raw, isNaN(fb) ? 80 : fb];
}

/**
 * True only for addresses that are actually reachable purely over a LAN
 * (RFC1918 ranges + loopback). A DDNS hostname or a public IP stored in the
 * "local_ip_address" field (a common config pattern for devices behind a home
 * router with no separate LAN IP on file) is NOT one of these — it resolves
 * over the public internet and deserves the same timeout budget as a public
 * address, not the tight LAN-only budget.
 */
export function isPrivateLanHost(host: string): boolean {
  const h = host.trim().toLowerCase();
  if (h === 'localhost') return true;
  const m = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return false; // hostnames (e.g. DDNS) are never treated as LAN-local
  const [a, b] = [parseInt(m[1], 10), parseInt(m[2], 10)];
  return (
    a === 127 ||
    a === 10 ||
    (a === 172 && b >= 16 && b <= 31) ||
    (a === 192 && b === 168)
  );
}

/**
 * Returns true if the Electron `deviceQuickProbe` IPC handler is registered
 * in this session. False in web browsers or old Electron builds that haven't restarted.
 */
/**
 * First address that is actually usable.
 *
 * An empty or whitespace-only string is "no address", not an address. `??` only skips
 * null/undefined, so `device.ip_address ?? device.local_ip_address` kept a stored '' — and an
 * empty ip_address is the normal shape for a device whose single address was typed into the
 * local field. The resulting '' then failed the `if (!ip)` guard and the whole selection
 * returned null, i.e. "this device has no address on file", for a device that had one.
 */
function firstUsableAddress(...candidates: Array<string | undefined>): string | null {
  for (const candidate of candidates) {
    if (candidate && candidate.trim() !== '') return candidate;
  }
  return null;
}

export function canTcpProbe(): boolean {
  return typeof window !== 'undefined' && !!(window as any).electronAPI?.deviceQuickProbe;
}

/**
 * Raw TCP connectivity test via Electron IPC.
 * Returns false immediately if probe is unavailable (does NOT throw).
 */
export async function tcpProbe(
  host: string,
  port: number,
  timeoutMs = 400
): Promise<boolean> {
  if (!canTcpProbe()) return false;
  try {
    return await (window as any).electronAPI.deviceQuickProbe(host, port, timeoutMs);
  } catch {
    return false;
  }
}

/**
 * Pick the best reachable IP address for a direct device operation.
 *
 * Both local_ip_address and ip_address are TCP-probed concurrently
 * (`timeoutLocal` / `timeoutPublic`, default 400ms / 1500ms) so the public
 * probe's budget isn't wasted waiting for the local probe to finish first.
 * local_ip_address is still preferred when both are reachable:
 *  1. local reachable → return { ip, source: 'local' }
 *  2. else public reachable → return { ip, source: 'public' }
 *  3. else → return null (caller should fall back to cloud)
 *
 * If TCP probe is unavailable (canTcpProbe() === false), skips probing and returns
 * the best-effort address with source='fallback' so callers can still attempt a
 * connection rather than silently failing.
 *
 * If TCP probe IS available and both addresses were actually probed and neither
 * responded, returns source='unreachable' (distinct from 'fallback') — callers
 * should NOT waste a long HTTP timeout retrying an address the probe already
 * proved dead; go straight to the cloud fallback instead.
 *
 * `retryOnUnreachable` re-probes once before accepting that verdict. Embedded device HTTP
 * servers accept very few concurrent connections and RST a SYN once their backlog is full, so a
 * single refusal is routinely transient — this is why an operation could fail and then succeed
 * on a manual retry seconds later. Operations should pass it; the polling that paints status
 * badges should not, or every refresh pays double probe latency for genuinely dead devices.
 */
export async function smartSelectDeviceIp(
  device: DeviceAddresses,
  options: {
    timeoutLocal?: number;
    timeoutPublic?: number;
    retryOnUnreachable?: boolean;
    retryDelayMs?: number;
  } = {}
): Promise<{ ip: string; source: 'local' | 'public' | 'fallback' | 'unreachable'; latencyMs: number } | null> {
  const {
    timeoutLocal = 400,
    timeoutPublic = 1500,
    retryOnUnreachable = false,
    retryDelayMs = 350,
  } = options;
  const start = Date.now();

  // No probe available — return best-effort address without probing.
  // Prefer PUBLIC IP: the health check also uses public IP when probe is unavailable,
  // so operations should target the same address to stay consistent.
  if (!canTcpProbe()) {
    const ip = firstUsableAddress(device.ip_address, device.local_ip_address);
    if (!ip) return null;
    return { ip, source: 'fallback', latencyMs: 0 };
  }

  // Kick off both probes at the same time so the public probe's timeout budget
  // isn't wasted waiting for the local probe to finish first. Local is still
  // preferred: we await it first, but since the public probe was already
  // started, awaiting it afterwards only waits for whatever time it has left.
  const localParsed = device.local_ip_address
    ? parseDeviceHostPort(device.local_ip_address, device.port)
    : null;
  const publicParsed = device.ip_address
    ? parseDeviceHostPort(device.ip_address, device.port)
    : null;

  // A DDNS hostname or public IP stored in local_ip_address (some devices only have
  // one address on file, entered in the "private" field) resolves over the internet —
  // give it the public timeout budget, not the tight LAN one, or it'll be marked
  // unreachable before DNS + WAN round-trip even completes.
  const localTimeout = localParsed && !isPrivateLanHost(localParsed[0]) ? timeoutPublic : timeoutLocal;

  // One round of concurrent probes. Returns the winning source, or null if neither answered.
  const probeRound = async (): Promise<'local' | 'public' | null> => {
    const localProbe = localParsed
      ? tcpProbe(localParsed[0], localParsed[1], localTimeout)
      : Promise.resolve(false);
    const publicProbe = publicParsed
      ? tcpProbe(publicParsed[0], publicParsed[1], timeoutPublic)
      : Promise.resolve(false);

    if (await localProbe) return 'local';
    if (await publicProbe) return 'public';
    return null;
  };

  let winner = await probeRound();

  // A device with nothing on file has nothing to re-probe.
  if (winner === null && retryOnUnreachable && (localParsed || publicParsed)) {
    if (retryDelayMs > 0) {
      await new Promise(resolve => setTimeout(resolve, retryDelayMs));
    }
    winner = await probeRound();
  }

  if (winner === 'local') {
    return { ip: device.local_ip_address as string, source: 'local', latencyMs: Date.now() - start };
  }

  if (winner === 'public') {
    return { ip: device.ip_address as string, source: 'public', latencyMs: Date.now() - start };
  }

  // Both probes actually ran and neither responded — the device is confirmed
  // unreachable on both addresses. Still hand back an address (public preferred)
  // so callers can log/attribute the cloud fallback, but tag it 'unreachable' so
  // they know NOT to retry it with a long HTTP timeout.
  //
  // null here must stay reserved for "no address on file": callers distinguish the two, and
  // conflating them made a probed-dead device take the "probe timed out, try the stored IP
  // anyway" path — retrying the exact address the probe had just rejected.
  const ip = firstUsableAddress(device.ip_address, device.local_ip_address);
  if (!ip) return null;
  return { ip, source: 'unreachable', latencyMs: Date.now() - start };
}

/**
 * Full device reachability check.
 *
 * 1. TCP probe (fast, Electron with deviceQuickProbe): local (~400ms) then public (~1500ms).
 * 2. HTTP GET fallback — always tried if TCP probe fails or is unavailable.
 *    Any HTTP response (200, 401, 403…) counts as reachable.
 */
export async function checkDeviceReachability(device: DeviceAddresses): Promise<{
  reachable: boolean;
  source?: 'local' | 'public';
  latencyMs: number;
}> {
  if (!device.local_ip_address && !device.ip_address) {
    return { reachable: false, latencyMs: 0 };
  }

  const start = Date.now();

  if (canTcpProbe()) {
    const result = await smartSelectDeviceIp(device, { timeoutLocal: 400, timeoutPublic: 1500 });
    if (result && (result.source === 'local' || result.source === 'public')) {
      return { reachable: true, source: result.source, latencyMs: result.latencyMs };
    }
    // Both TCP probes failed.
    // If the device has no public IP AND its local_ip_address is a genuine LAN-private
    // address, don't attempt HTTP to it — it'll ETIMEDOUT and pollute the logs with 6s
    // hangs per device (local-only devices are simply unreachable off-LAN). But a DDNS
    // hostname / public IP stored in local_ip_address (some devices only have one address
    // on file) IS worth an HTTP retry — some routers allow HTTP through NAT but block raw
    // TCP probes.
    const localParsedForHttp = device.local_ip_address
      ? parseDeviceHostPort(device.local_ip_address, device.port)
      : null;
    if (!device.ip_address && (!localParsedForHttp || isPrivateLanHost(localParsedForHttp[0]))) {
      return { reachable: false, latencyMs: Date.now() - start };
    }
  }

  // HTTP fallback — reached when: probe unavailable, probe failed, or dev hot-reload mismatch.
  // Prefer public IP: if local probe failed we're likely remote, so local IP will ETIMEDOUT.
  const httpApi = (window as any)?.electronAPI?.deviceHttpRequest;
  if (!httpApi) return { reachable: false, latencyMs: 0 };

  const ip = device.ip_address ?? device.local_ip_address;
  if (!ip) return { reachable: false, latencyMs: 0 };

  const parsed = parseDeviceHostPort(ip, device.port);
  if (!parsed) return { reachable: false, latencyMs: 0 };

  const [host, port] = parsed;
  const protocol = port === 443 ? 'https' : 'http';
  const url = `${protocol}://${host}:${port}`;
  const source: 'local' | 'public' = (ip === device.ip_address) ? 'public' : 'local';

  try {
    const result = await httpApi({ url, method: 'GET', timeout: 6000 });
    const reachable = !!(result?.success || typeof result?.statusCode === 'number');
    return { reachable, source, latencyMs: Date.now() - start };
  } catch {
    return { reachable: false, latencyMs: Date.now() - start };
  }
}
